import React, { useState } from 'react';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    ListItemButton,
    MenuItem,
    Radio,
    RadioGroup,
    Slider,
    styled
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import AdditionalInstructors from './AdditionalInstructors';
import UnauthUserAccessClassSetting from './UnauthUserAccessClassSetting';
import { Collapsible } from '../../../../components/common/collapsible';
import ControlledSelect from '../../../../components/forms/ControlledSelect';
import { Error, StyledSubtext } from '../../../../components/forms/styles';
import { ClassJoinPermission, WebExpOptions } from '../../../../types/adminConfig';
import { ContentLearningTools } from '../../../../types/apiTypes';
import { Permission } from '../../../../types/recordingTypes';
import { ClassContainer } from '../../ClassContainer';
import TemplateOptions from '../../TemplateOptions';
import { CanJoinBeforeInstructor, RequireAuth, WaitingRoom } from '../../types';
import { showSettings } from '../../utils';

const ExpandMoreButton = styled(ListItemButton)`
    cursor: pointer;
    display: flex;
    justify-content: flex-start;
    font-size: 1rem;

    padding: 1rem 0.5rem;

    border: 0;
    outline: 0;
    border-radius: 4px;

    .MuiButtonBase-root:hover {
        background-color: transparent;
    }
`;

const SectionTitle = styled('div')`
    font-weight: 600;
    margin-left: 0.5rem;
`;

const StyledFormControl = styled(FormControl)`
    padding-right: 24px;
`;

const StyledFullWidthContainer = styled('div')`
    grid-column: span 2;
`;

const AdvancedOptionsContainer = styled('div')`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-row-gap: 1rem;
`;

export interface ClassFormAdvancedProps {
    parentPage: 'admin' | 'scheduler';
}

const marks = [
    { value: 15, label: '15' },
    { value: 30, label: '30' },
    { value: 45, label: '45' },
    { value: 60, label: '60' }
];

const Advanced: React.FC<ClassFormAdvancedProps> = ({ parentPage }) => {
    const { t } = useTranslation('calendar');
    const { t: tAdminSettings } = useTranslation('admin_settings');
    const {
        control,
        publishedTemplates,
        formActionType,
        allowForcedAuthentication,
        zoomAppId,
        ltiState,
        lmsSyncSettings,
        schoolOverrides,
        classRecord
    } = useContainer(ClassContainer);

    const isTeams = zoomAppId === 'teams';

    const baseShowSettingsOpts = {
        overrides: schoolOverrides,
        parentPage
    };

    const showUnauthUserAccessOptions = showSettings({
        ...baseShowSettingsOpts,
        overrideName: 'unauthUserAccess'
    });

    const showAllowVanillaZoom = showSettings({
        ...baseShowSettingsOpts,
        overrideName: 'allowVanillaZoom'
    });
    const showClassJoinPermission = showSettings({
        ...baseShowSettingsOpts,
        overrideName: 'classJoinPermission'
    });
    const showRosterSelfEnrollment = showSettings({
        ...baseShowSettingsOpts,
        overrideName: 'rosterSelfEnrollment'
    });
    const showAiTa = showSettings({
        ...baseShowSettingsOpts,
        overrideName: 'hasAiTa'
    });
    const showFileAnnotations = showSettings({
        ...baseShowSettingsOpts,
        overrideName: 'hasFileAnnotations'
    });
    const showPresentations = showSettings({
        ...baseShowSettingsOpts,
        overrideName: 'hasPresentations'
    });
    const showMobile = showSettings({
        ...baseShowSettingsOpts,
        overrideName: 'allowNextGenMobile'
    });
    const showEnablePrivateDiscussion = showSettings({
        ...baseShowSettingsOpts,
        overrideName: 'enablePrivateDiscussion'
    });
    const showRecordingInitialPermission = showSettings({
        ...baseShowSettingsOpts,
        overrideName: 'recordingInitialPermission'
    });
    const showRecordingsRestrictChatParticipants = showSettings({
        ...baseShowSettingsOpts,
        overrideName: 'recordingsRestrictChatParticipants'
    });

    const showTelephony =
        isTeams &&
        showSettings({
            ...baseShowSettingsOpts,
            overrideName: 'hasTelephony'
        });
    const showWebExperienceToggle =
        !isTeams &&
        showSettings({
            ...baseShowSettingsOpts,
            overrideName: 'webExperience'
        });

    const [expanded, setExpanded] = useState(false);

    const handleClick = () => {
        setExpanded((previous) => !previous);
    };

    const showTemplateOption = publishedTemplates.length > 0 && formActionType === 'CREATE';
    const showAdditionalInstructors = formActionType === 'CREATE';
    const showContentTypeOption =
        lmsSyncSettings.sync_enabled &&
        lmsSyncSettings.enable_content &&
        lmsSyncSettings.content_learning_tools === ContentLearningTools.InstructorChoice &&
        (Object.keys(ltiState).length !== 0 || classRecord?.parent);
    const showGradeTardyScore = lmsSyncSettings.sync_enabled && lmsSyncSettings.enable_attendance_grade;
    const showStudentJoinPriorMinutes = showSettings({
        ...baseShowSettingsOpts,
        overrideName: 'allowStudentJoinPriorMinutes'
    });

    return (
        <>
            <ExpandMoreButton aria-controls="advanced-section" onClick={handleClick}>
                {expanded ? <ExpandLess /> : <ExpandMore />}
                <SectionTitle>{t('advanced-settings')}</SectionTitle>
            </ExpandMoreButton>
            <Collapsible id="advanced-section" expanded={expanded}>
                {showTemplateOption && (
                    <div>
                        <Controller
                            name="courseTemplate"
                            control={control}
                            render={(props) => (
                                <>
                                    <FormControlLabel
                                        label={t('use-a-course-template')}
                                        control={<Checkbox {...props.field} />}
                                    />
                                    {props?.fieldState?.error ? <Error>{props.fieldState.error.message}</Error> : null}
                                </>
                            )}
                        />
                        <TemplateOptions />
                    </div>
                )}
                <AdvancedOptionsContainer>
                    {showContentTypeOption && (
                        <StyledFormControl>
                            <ControlledSelect
                                defaultValue="lms"
                                name="contentType"
                                label={t('content-type')}
                                control={control}
                            >
                                <MenuItem value={'native'}>{t('content-type-class')}</MenuItem>
                                <MenuItem value={'lms'}>{t('content-type-lms')}</MenuItem>
                            </ControlledSelect>
                        </StyledFormControl>
                    )}
                    {!isTeams && zoomAppId !== undefined && (
                        <Controller
                            control={control}
                            name="waitingRoom"
                            render={({ field }) => (
                                <div>
                                    <FormLabel id="waiting-room-toggle">{t('waiting-room')}</FormLabel>
                                    <RadioGroup aria-labelledby="waiting-room-toggle" {...field} row>
                                        <FormControlLabel value={WaitingRoom.ON} control={<Radio />} label={t('on')} />
                                        <FormControlLabel
                                            value={WaitingRoom.OFF}
                                            control={<Radio />}
                                            label={t('off')}
                                        />
                                    </RadioGroup>
                                </div>
                            )}
                        />
                    )}
                    <Controller
                        control={control}
                        name="canJoinBeforeInstructor"
                        render={({ field }) => (
                            <div>
                                <FormLabel id="can-join-before-instructor">
                                    {t('students-can-join-before-instructor')}
                                </FormLabel>
                                <RadioGroup aria-labelledby="can-join-before-instructor" {...field} row>
                                    <FormControlLabel
                                        value={CanJoinBeforeInstructor.YES}
                                        control={<Radio />}
                                        label={t('yes')}
                                    />
                                    <FormControlLabel
                                        value={CanJoinBeforeInstructor.NO}
                                        control={<Radio />}
                                        label={t('no')}
                                    />
                                </RadioGroup>
                            </div>
                        )}
                    />
                    {showStudentJoinPriorMinutes && (
                        <StyledFullWidthContainer>
                            <InputLabel id="student-join-prior-minutes">
                                {tAdminSettings('configuration.join-options.student')}
                            </InputLabel>
                            <Controller
                                control={control}
                                name="allowStudentJoinPriorMinutes"
                                render={({ field }) => (
                                    <div>
                                        <Slider
                                            defaultValue={field.value || 0}
                                            marks={marks}
                                            aria-labelledby="student-join-prior-minutes"
                                            getAriaLabel={(value) =>
                                                `${value} ${tAdminSettings('configuration.minutes')}`
                                            }
                                            valueLabelDisplay="auto"
                                            step={5}
                                            min={0}
                                            max={60}
                                            {...field}
                                            sx={{ width: '80%' }}
                                        />
                                    </div>
                                )}
                            />
                        </StyledFullWidthContainer>
                    )}
                    {showGradeTardyScore && (
                        <Controller
                            control={control}
                            name="attendanceGradeTardyScore"
                            render={({ field }) => (
                                <div>
                                    <InputLabel id="lms-attendance-tardy-score-label">
                                        {t('lms-attendance-sync-tardy-score')}
                                    </InputLabel>
                                    <StyledSubtext id={`helper_tardy_score`}>
                                        {t('lms-attendance-sync-tardy-score-helper')}
                                    </StyledSubtext>
                                    <Slider
                                        defaultValue={
                                            field.value || lmsSyncSettings?.attendance_grade_tardy_score || 80
                                        }
                                        aria-labelledby="lms-attendance-tardy-score-label"
                                        getAriaValueText={(value) => `${value}%`}
                                        valueLabelDisplay="on"
                                        step={1}
                                        min={0}
                                        max={100}
                                        {...field}
                                    />
                                </div>
                            )}
                        />
                    )}
                    {allowForcedAuthentication && (
                        <Controller
                            control={control}
                            name="requireAuth"
                            render={({ field }) => (
                                <div>
                                    <FormLabel id="require-auth">{t('require-authentication')}</FormLabel>
                                    <RadioGroup aria-labelledby="require-auth" {...field} row>
                                        <FormControlLabel value={RequireAuth.ON} control={<Radio />} label={t('on')} />
                                        <FormControlLabel
                                            value={RequireAuth.OFF}
                                            control={<Radio />}
                                            label={t('off')}
                                        />
                                    </RadioGroup>
                                </div>
                            )}
                        />
                    )}
                    {showWebExperienceToggle && (
                        <Controller
                            control={control}
                            name="webExperience"
                            render={({ field }) => (
                                <div>
                                    <FormLabel id="web-experience-label">{t('web-experience')}</FormLabel>

                                    <RadioGroup aria-labelledby="web-experience-label" {...field} row>
                                        <FormControlLabel
                                            value={WebExpOptions.Classic}
                                            control={<Radio />}
                                            label={t('classic')}
                                        />
                                        <FormControlLabel
                                            value={WebExpOptions.NextGen}
                                            control={<Radio />}
                                            label={t('nextgen')}
                                        />
                                    </RadioGroup>
                                </div>
                            )}
                        />
                    )}
                    {showRecordingInitialPermission && (
                        <div>
                            <FormLabel id="recording-initial-permission">
                                {tAdminSettings('configuration.recordings.permission')}
                            </FormLabel>

                            <ControlledSelect
                                sx={{ display: 'block' }}
                                labelId="recording-initial-permission"
                                id="recordingInitialPermission"
                                control={control}
                                name="recordingInitialPermission"
                            >
                                <MenuItem value={Permission.NOOVERRIDE}>
                                    {t('lti:recordings.permissions.none')}
                                </MenuItem>
                                <MenuItem value={Permission.ADMINS}>{t('lti:recordings.permissions.admins')}</MenuItem>
                                <MenuItem value={Permission.ROSTER}>{t('lti:recordings.permissions.roster')}</MenuItem>
                                <MenuItem value={Permission.PUBLIC}>{t('lti:recordings.permissions.public')}</MenuItem>
                            </ControlledSelect>
                        </div>
                    )}
                    {showRecordingsRestrictChatParticipants && (
                        <Controller
                            control={control}
                            name="recordingsRestrictChatParticipants"
                            render={({ field }) => (
                                <StyledFullWidthContainer>
                                    <FormControlLabel
                                        label={tAdminSettings('configuration.recordings.restrict-chat-participants')}
                                        control={<Checkbox checked={field.value} {...field} />}
                                    />
                                </StyledFullWidthContainer>
                            )}
                        />
                    )}
                    {showClassJoinPermission && (
                        <Controller
                            control={control}
                            name="classJoinPermission"
                            render={({ field }) => (
                                <div>
                                    <FormLabel id="class-join-permission">
                                        {tAdminSettings('auth.whoCanJoin')}
                                    </FormLabel>

                                    <RadioGroup aria-labelledby="class-join-permission" {...field} row>
                                        <FormControlLabel
                                            value={ClassJoinPermission.All}
                                            control={<Radio />}
                                            label={tAdminSettings('auth.allUsers')}
                                        />
                                        <FormControlLabel
                                            value={ClassJoinPermission.RosterOnly}
                                            control={<Radio />}
                                            label={tAdminSettings('auth.onlyRoster')}
                                        />
                                        <FormControlLabel
                                            value={ClassJoinPermission.AuthenticatedUsers}
                                            control={<Radio />}
                                            label={tAdminSettings('auth.usersSignedIn')}
                                        />
                                    </RadioGroup>
                                </div>
                            )}
                        />
                    )}
                    {showAllowVanillaZoom && (
                        <Controller
                            control={control}
                            name="allowVanillaZoom"
                            render={({ field }) => (
                                <StyledFullWidthContainer>
                                    <FormControlLabel
                                        label={tAdminSettings('configuration.allow-vanilla-zoom')}
                                        control={<Checkbox checked={field.value} {...field} />}
                                    />
                                </StyledFullWidthContainer>
                            )}
                        />
                    )}
                    {showRosterSelfEnrollment && (
                        <Controller
                            control={control}
                            name="rosterSelfEnrollment"
                            render={({ field }) => (
                                <StyledFullWidthContainer>
                                    <FormControlLabel
                                        label={tAdminSettings('auth.selfEnrollment')}
                                        control={<Checkbox checked={field.value} {...field} />}
                                    />
                                </StyledFullWidthContainer>
                            )}
                        />
                    )}
                    {showAiTa && (
                        <Controller
                            control={control}
                            name="hasAiTa"
                            render={({ field }) => (
                                <StyledFullWidthContainer>
                                    <FormControlLabel
                                        label={tAdminSettings('configuration.allow-ai-ta')}
                                        control={<Checkbox checked={field.value} {...field} />}
                                    />
                                </StyledFullWidthContainer>
                            )}
                        />
                    )}
                    {showMobile && (
                        <Controller
                            control={control}
                            name="allowNextGenMobile"
                            render={({ field }) => (
                                <div>
                                    <FormLabel>{tAdminSettings('configuration.allow-mobile')}</FormLabel>
                                    <RadioGroup
                                        {...field}
                                        row
                                        // Ensure the value is passed as a boolean
                                        value={String(field.value) === 'true' || field.value === true}
                                        onChange={(e) => field.onChange(e.target.value === 'true')}
                                    >
                                        <FormControlLabel value={false} control={<Radio />} label={t('classic')} />
                                        <FormControlLabel
                                            value={true}
                                            control={<Radio />}
                                            label={tAdminSettings('configuration.nextgen-mobile')}
                                        />
                                    </RadioGroup>
                                </div>
                            )}
                        />
                    )}
                    {showFileAnnotations && (
                        <Controller
                            control={control}
                            name="hasFileAnnotations"
                            render={({ field }) => (
                                <StyledFullWidthContainer>
                                    <FormControlLabel
                                        label={tAdminSettings('configuration.allow-file-annotations')}
                                        control={<Checkbox checked={field.value} {...field} />}
                                    />
                                </StyledFullWidthContainer>
                            )}
                        />
                    )}
                    {showPresentations && (
                        <Controller
                            control={control}
                            name="hasPresentations"
                            render={({ field }) => (
                                <StyledFullWidthContainer>
                                    <FormControlLabel
                                        label={tAdminSettings('configuration.allow-presentations')}
                                        control={<Checkbox checked={field.value} {...field} />}
                                    />
                                </StyledFullWidthContainer>
                            )}
                        />
                    )}
                    {showEnablePrivateDiscussion && (
                        <Controller
                            control={control}
                            name="enablePrivateDiscussion"
                            render={({ field }) => (
                                <StyledFullWidthContainer>
                                    <FormControlLabel
                                        label={tAdminSettings('configuration.enable-private-discussion')}
                                        control={<Checkbox checked={field.value} {...field} />}
                                    />
                                </StyledFullWidthContainer>
                            )}
                        />
                    )}
                    {showUnauthUserAccessOptions && (
                        <StyledFullWidthContainer>
                            <UnauthUserAccessClassSetting />
                        </StyledFullWidthContainer>
                    )}

                    {showAdditionalInstructors && (
                        <StyledFullWidthContainer>
                            <AdditionalInstructors />
                        </StyledFullWidthContainer>
                    )}
                    {showTelephony && (
                        <Controller
                            control={control}
                            name="hasTelephony"
                            render={({ field }) => (
                                <StyledFullWidthContainer>
                                    <FormControlLabel
                                        label={tAdminSettings('configuration.allowTelephony')}
                                        control={<Checkbox checked={field.value} {...field} />}
                                    />
                                </StyledFullWidthContainer>
                            )}
                        />
                    )}
                </AdvancedOptionsContainer>
            </Collapsible>
        </>
    );
};

export default Advanced;
